/*
 * Voyado page surfaces — IIFE bound to [data-voyado-landing-page] (collection
 * PLP) and [data-voyado-search-page] (search results). Both delegate all
 * rendering/state to VoyadoPlp (voyado-plp-controller.js); only the fetch source
 * differs (landingPage by collection path vs searchPage by ?q=). Follows the
 * theme's section-JS lifecycle: init on load + shopify:section:load, teardown on
 * shopify:section:unload. Loaded once per page from the two sections.
 */
;(function () {
  'use strict';

  const controllers = new WeakMap();

  // Empty search has no phrase (Elevate's search-page requires one — empty q = 400), so fall back to
  // this collection landing-page: a bare /search shows the filter bar + a default grid, not nothing.
  const DEFAULT_PAGE_REFERENCE = '/all';

  // Store the controller per node so shopify:section:unload can tear it down;
  // the WeakMap guards against double-attach.
  function attach(node, opts) {
    if (controllers.has(node)) return;
    controllers.set(node, window.VoyadoPlp.attach(node, opts));
  }

  function initLanding(node) {
    const handle = node.getAttribute('data-collection-handle');
    const pageReference = handle ? '/' + handle : null;
    if (!pageReference) { node.setAttribute('data-voyado-state', 'empty'); return; }
    attach(node, {
      errorState: 'empty',
      fetch: function (p) {
        return window.VoyadoClient.landingPage(Object.assign({ pageReference: pageReference, presentCustom: 'swatch_image_url|product_badges|colour_name' }, p));
      }
    });
  }

  // Reflect an Elevate applied autocorrection in the search UI without a re-query:
  // Elevate already returned results for the corrected phrase, so we only sync the
  // displayed term — the URL ?q=, the header echo and the search input. These are
  // existing elements; only text/value/URL change (no markup change).
  function applySearchTermToUi(term) {
    try {
      const u = new URL(location.href);
      u.searchParams.set('q', term);
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    } catch (e) { /* opaque origin / old browser */ }
    const echo = document.querySelector('[data-voyado-search-echo]');
    if (echo) echo.textContent = '"' + term + '"';
    const form = document.querySelector('[data-voyado-search-form]');
    const input = form && form.querySelector('input[name="q"]');
    if (input) input.value = term;
  }

  function initSearch(node) {
    const params = new URLSearchParams(location.search);
    let q = params.get('q') || '';
    const origin = params.get('origin') || 'ORGANIC';
    const originQ = q; // the phrase `origin` describes; a re-typed phrase resets to ORGANIC (in fetch below)
    // `origin` is transport, not shareable state — strip it from the address bar once read so a
    // refresh/share of the URL doesn't re-report a stale origin (it falls back to ORGANIC).
    if (params.has('origin')) {
      try {
        const u = new URL(location.href);
        u.searchParams.delete('origin');
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      } catch (e) { /* opaque origin / old browser */ }
    }
    // As-you-type queries run as previews (notify=false), so partial terms don't register
    // in Elevate's search analytics / behavioural data (per the search-page query docs);
    // the committed search (Enter, or the initial page load) registers normally.
    let liveTyping = false;

    // Is the in-flight query a live-typing preview? Read when onAutoCorrect resolves, so a correction
    // is only applied to the UI on a committed search — never mid-typing (which would move the cursor).
    let previewQuery = false;

    const opts = {
      fetch: function (p) {
        // No term → the default grid via a landing-page (DEFAULT_PAGE_REFERENCE). Same primaryList
        // shape as search, so the controller renders it identically; facets/sort (p) still apply.
        if (!q.trim()) {
          previewQuery = false;
          return window.VoyadoClient.landingPage(Object.assign({ pageReference: DEFAULT_PAGE_REFERENCE, presentCustom: 'swatch_image_url|product_badges|colour_name' }, p));
        }
        // origin describes how the *phrase* was reached — only valid while q is unchanged.
        // A re-typed phrase (q !== originQ) is organic, so don't carry the stale origin.
        const qp = Object.assign({ q: q, origin: (q === originQ ? origin : 'ORGANIC'), presentCustom: 'swatch_image_url|product_badges|colour_name' }, p);
        previewQuery = liveTyping;
        if (liveTyping) qp.notify = 'false';
        return window.VoyadoClient.searchPage(qp);
      },
      // Applied autocorrection: only on a committed search (Enter / the Search button / the initial
      // page load) — sync the displayed term + keep subsequent queries (facets/sort) on the corrected
      // phrase so Elevate doesn't re-correct in a loop. Skipped for live-typing previews.
      onAutoCorrect: function (corrected) {
        if (previewQuery) return;
        if (!corrected || corrected === q) return;
        q = corrected;
        applySearchTermToUi(corrected);
      }
    };

    // Attach unconditionally: the first load fetches search results for a term, or the default grid
    // when the term is empty (see fetch) — so a bare /search still shows the filter bar + products.
    attach(node, opts);

    // Instant search — live-update the grid as the visitor types in the search field
    // (debounced). load(true) re-queries with the new term and reuses any committed
    // facets; the header count + grid stay in sync via the controller's render. The form
    // still GET-submits when JS is off.
    const input = document.querySelector('[data-voyado-search-form] input[name="q"]');
    if (input) {
      let timer;
      const apply = function (track) {
        const val = input.value.trim();
        q = val;
        try {
          const u = new URL(location.href);
          if (val) { u.searchParams.set('q', val); } else { u.searchParams.delete('q'); }
          history.replaceState(history.state, '', u.pathname + u.search + u.hash);
        } catch (e) { /* opaque origin */ }
        const echo = document.querySelector('[data-voyado-search-echo]');
        if (echo) echo.textContent = val ? '"' + val + '"' : 'All';   // no term → browsing the default /all grid
        // A term runs a search preview while typing (notify=false); an empty term isn't a preview —
        // the fetch falls back to the default grid (no notify). Either way, re-query and keep facets.
        liveTyping = !track && !!val;
        if (controllers.has(node)) controllers.get(node).load(true);  // re-query (term → search, empty → default grid)
        else attach(node, opts);                                      // first query → attach + load
        liveTyping = false;
      };
      input.addEventListener('input', function () { clearTimeout(timer); timer = setTimeout(function () { apply(false); }, 350); });
      const form = input.closest('form');
      // Submit (the magnifying-glass button or the mobile keyboard's Search key): commit the search,
      // and on touch also blur the field so the on-screen keyboard closes — skipped on desktop, where
      // a blur would just drop focus. Results already update live while typing.
      if (form) form.addEventListener('submit', function (e) {
        e.preventDefault(); clearTimeout(timer); apply(true);
        if (window.matchMedia('(pointer: coarse)').matches) input.blur();
      });
    }
  }

  function initAll(root) {
    const r = root || document;
    r.querySelectorAll('[data-voyado-landing-page]').forEach(initLanding);
    r.querySelectorAll('[data-voyado-search-page]').forEach(initSearch);
  }

  function teardown(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-voyado-landing-page], [data-voyado-search-page]').forEach(function (node) {
      const ctl = controllers.get(node);
      if (ctl) { ctl.destroy(); controllers.delete(node); }
    });
  }

  // voyado-client.js now loads in <head>, ahead of this section-embedded module script in
  // document order (see layout/theme.liquid), so window.VoyadoClient already exists by the time
  // this line runs — no need to wait for DOMContentLoaded first. That wait used to sit between the
  // PLP/search's first product query and everything else deferred later in the document (L-4 in
  // the ).
  initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
})();
